export interface ColumnVisibilityOption {
  id: string;
  label: string;
  columnIndex: number;
}

export class ColumnVisibilityHelper {
  constructor(
    private readonly storageKey: string,
    private readonly tableClass: string,
    private readonly columns: ColumnVisibilityOption[]
  ) { }

  getDefaultVisibleColumnIds(): Set<string> {
    return new Set<string>(this.columns.map(column => column.id));
  }

  loadVisibleColumnIds(): Set<string> {
    const defaultColumns = this.getDefaultVisibleColumnIds();

    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) return defaultColumns;

      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return defaultColumns;

      const validIds = parsed.filter((columnId: string) =>
        this.columns.some(column => column.id === columnId)
      );

      return validIds.length > 0
        ? new Set<string>(validIds)
        : defaultColumns;
    } catch {
      return defaultColumns;
    }
  }

  persistVisibleColumnIds(visibleColumnIds: Set<string>): void {
    try {
      localStorage.setItem(
        this.storageKey,
        JSON.stringify(Array.from(visibleColumnIds))
      );
    } catch {
      // Ignore storage failures.
    }
  }

  buildCss(visibleColumnIds: Set<string>): string {
    const hiddenSelectors = this.columns
      .filter(column => !visibleColumnIds.has(column.id))
      .map(
        column => `.${this.tableClass} th:nth-child(${column.columnIndex}), .${this.tableClass} td:nth-child(${column.columnIndex})`
      );

    if (hiddenSelectors.length === 0) return '';

    return `${hiddenSelectors.join(', ')} { display: none !important; }`;
  }

  toggleColumn(visibleColumnIds: Set<string>, columnId: string): Set<string> {
    const next = new Set<string>(visibleColumnIds);

    if (next.has(columnId)) {
      if (next.size === 1) return next;
      next.delete(columnId);
    } else {
      next.add(columnId);
    }

    return next;
  }

  isVisible(visibleColumnIds: Set<string>, columnId: string): boolean {
    return visibleColumnIds.has(columnId);
  }
}
